import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Copy, ArrowRight, Radio, DollarSign, Users, Zap } from "lucide-react";
import { toast } from "sonner";

/*  /streamer — public application page for the WasntMe Streamer Program.
    15% rev share on every pack sold through the streamer's /packs?ref=CODE
    link. One-click apply → instant code → shareable link + dashboard.
*/
const Streamer = () => {
  const [handle, setHandle]       = useState("");
  const [platform, setPlatform]   = useState("twitch");
  const [email, setEmail]         = useState("");
  const [audience, setAudience]   = useState("");
  const [links, setLinks]         = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult]       = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!handle.trim() || !email.trim()) {
      toast.error("Handle and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/streamer/apply", {
        handle: handle.trim().replace(/^@+/, ""),
        platform,
        email: email.trim(),
        audience: audience.trim(),
        links: links.trim(),
      });
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (txt) => {
    try { navigator.clipboard.writeText(txt); toast.success("Copied."); } catch {}
  };

  return (
    <div className="min-h-screen bg-[#0a0908] text-[#f4f0ec] px-4 sm:px-6 py-10 sm:py-16" data-testid="streamer-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="text-[10px] tracking-[0.3em] text-[#5a5050] hover:text-[#d4af37]">← wasntme.party</Link>

        <div className="mt-6 mb-10">
          <div className="text-[10px] tracking-[0.4em] text-[#8b0000] mb-3">THE STREAMER PROGRAM</div>
          <h1 className="font-display text-4xl sm:text-6xl text-[#f4f0ec] leading-tight mb-4">
            Run a case live. <span className="text-[#d4af37]">Earn 15% on every pack.</span>
          </h1>
          <p className="text-sm sm:text-base text-[#a39797] leading-relaxed max-w-2xl">
            You stream. Your chat votes on the killer. Every viewer who grabs a pack through your link earns you a cut — forever, on every one of their future buys. No gatekeeping, no application queue, instant code.
          </p>
        </div>

        {/* The three pillars */}
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          <div className="card-noir p-5" data-testid="streamer-pillar-share">
            <DollarSign size={18} className="text-[#d4af37] mb-2" />
            <div className="text-xs tracking-[0.2em] text-[#d4af37] mb-1">REV SHARE</div>
            <div className="text-sm text-[#f4f0ec] leading-relaxed">15% of every pack sold through your /packs?ref=CODE link. Attribution is lifetime per buyer.</div>
          </div>
          <div className="card-noir p-5" data-testid="streamer-pillar-content">
            <Zap size={18} className="text-[#d4af37] mb-2" />
            <div className="text-xs tracking-[0.2em] text-[#d4af37] mb-1">INSTANT CONTENT</div>
            <div className="text-sm text-[#f4f0ec] leading-relaxed">We send you a free Crew Pack kit. Your chat votes between rounds. Reveal is a 20-second ending clip.</div>
          </div>
          <div className="card-noir p-5" data-testid="streamer-pillar-audience">
            <Users size={18} className="text-[#d4af37] mb-2" />
            <div className="text-xs tracking-[0.2em] text-[#d4af37] mb-1">AUDIENCE FIT</div>
            <div className="text-sm text-[#f4f0ec] leading-relaxed">Works for Twitch, YouTube, TikTok Live, Kick. Ages 13+. Teen-safe cases available.</div>
          </div>
        </div>

        {/* The form OR the result */}
        {!result ? (
          <form onSubmit={submit} className="card-noir p-6 sm:p-8 space-y-5" data-testid="streamer-form">
            <div>
              <div className="text-xs tracking-[0.3em] text-[#d4af37] mb-4">APPLY — INSTANT APPROVAL</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] tracking-[0.3em] text-[#a39797] block mb-1.5">YOUR HANDLE</label>
                  <input
                    value={handle} onChange={(e) => setHandle(e.target.value)}
                    placeholder="@yourhandle"
                    maxLength={60}
                    className="w-full bg-[#1a1515] border border-[#2a2525] rounded-md px-3 py-2.5 text-sm text-[#f4f0ec] focus:border-[#d4af37] outline-none"
                    data-testid="streamer-handle"
                  />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.3em] text-[#a39797] block mb-1.5">PLATFORM</label>
                  <select
                    value={platform} onChange={(e) => setPlatform(e.target.value)}
                    className="w-full bg-[#1a1515] border border-[#2a2525] rounded-md px-3 py-2.5 text-sm text-[#f4f0ec] focus:border-[#d4af37] outline-none"
                    data-testid="streamer-platform"
                  >
                    <option value="twitch">Twitch</option>
                    <option value="youtube">YouTube</option>
                    <option value="tiktok">TikTok</option>
                    <option value="kick">Kick</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[10px] tracking-[0.3em] text-[#a39797] block mb-1.5">EMAIL (PAYOUTS)</label>
                  <input
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@domain.com"
                    className="w-full bg-[#1a1515] border border-[#2a2525] rounded-md px-3 py-2.5 text-sm text-[#f4f0ec] focus:border-[#d4af37] outline-none"
                    data-testid="streamer-email"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[10px] tracking-[0.3em] text-[#a39797] block mb-1.5">AUDIENCE (ONE LINE)</label>
                  <input
                    value={audience} onChange={(e) => setAudience(e.target.value)}
                    placeholder="e.g. 12k Twitch mystery fans, mostly US/EU, weeknight streams"
                    maxLength={200}
                    className="w-full bg-[#1a1515] border border-[#2a2525] rounded-md px-3 py-2.5 text-sm text-[#f4f0ec] focus:border-[#d4af37] outline-none"
                    data-testid="streamer-audience"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[10px] tracking-[0.3em] text-[#a39797] block mb-1.5">LINKS (OPTIONAL)</label>
                  <textarea
                    value={links} onChange={(e) => setLinks(e.target.value)}
                    placeholder="twitch.tv/yourhandle&#10;youtube.com/@yourhandle"
                    maxLength={400} rows={2}
                    className="w-full bg-[#1a1515] border border-[#2a2525] rounded-md px-3 py-2.5 text-sm text-[#f4f0ec] focus:border-[#d4af37] outline-none"
                    data-testid="streamer-links"
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-[#2a2525]">
              <div className="text-[10px] text-[#5a5050]">By submitting, you agree to the Streamer T&Cs. Payouts monthly via your email.</div>
              <button
                type="submit" disabled={submitting}
                className="btn-primary"
                data-testid="streamer-submit"
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <Radio size={14} />}
                {submitting ? "Submitting…" : "Get my code"}
              </button>
            </div>
          </form>
        ) : (
          <div className="card-noir p-6 sm:p-8 space-y-5" data-testid="streamer-result">
            <div>
              <div className="text-xs tracking-[0.3em] text-[#d4af37] mb-2">APPROVED · WELCOME, {result.handle?.toUpperCase()}</div>
              <div className="font-display text-2xl text-[#f4f0ec] leading-tight">You're in. Here's your toolkit.</div>
            </div>
            <div className="space-y-3">
              <div>
                <div className="text-[10px] tracking-[0.3em] text-[#a39797] mb-1.5">YOUR CODE</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-[#1a1515] border border-[#d4af37]/40 px-3 py-2.5 rounded-md text-lg tracking-[0.2em] text-[#d4af37]" data-testid="streamer-code">
                    {result.code}
                  </code>
                  <button onClick={() => copy(result.code)} className="btn-ghost text-xs" data-testid="streamer-copy-code">
                    <Copy size={13} /> Copy
                  </button>
                </div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.3em] text-[#a39797] mb-1.5">YOUR LANDING LINK (share this)</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-[#1a1515] border border-[#2a2525] px-3 py-2.5 rounded-md text-xs text-[#f4f0ec] break-all" data-testid="streamer-link">
                    {result.landing_url}
                  </code>
                  <button onClick={() => copy(result.landing_url)} className="btn-ghost text-xs" data-testid="streamer-copy-link">
                    <Copy size={13} /> Copy
                  </button>
                </div>
              </div>
            </div>
            <div className="pt-4 border-t border-[#2a2525] flex items-center justify-between flex-wrap gap-3">
              <div className="text-xs text-[#a39797]">
                Share rate: <span className="text-[#d4af37] font-semibold">{result.share_pct}%</span> of each sale, forever.
              </div>
              <Link
                to={`/streamer/dashboard?code=${result.code}`}
                className="btn-primary"
                data-testid="streamer-goto-dashboard"
              >
                Open my dashboard <ArrowRight size={13} />
              </Link>
            </div>
          </div>
        )}

        {/* The ask */}
        <div className="mt-10 text-xs text-[#5a5050] leading-relaxed max-w-2xl">
          Fine print, honestly: your 15% applies to the final paid amount per pack order (post-discount). Payouts process the first week of each month for the prior month's attributed sales. Attribution is permanent per buyer email — their next buy still counts, even without the ?ref link. No minimum followers. Teen-safe cases are clearly labeled; adult cases are 18+.
        </div>
      </div>
    </div>
  );
};

export default Streamer;
